import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import {
  createPlannerObservationProgram,
  createPlannerObservationQuestion,
  deletePlannerObservationProgram,
  deletePlannerObservationQuestion,
  fetchPlannerObservationPrograms,
  updatePlannerObservationProgram,
  updatePlannerObservationQuestion
} from 'api'
import { showToast } from 'app/helpers'

import QotSetup from './QotSetup'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

/**
 * Admin config for the org-level Observations: programmes, their Yes/No questions,
 * and the course-name keyword that resolves each programme's teacher dropdown.
 */
const ObservationSetup = () => {
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState('')
  const [progDialog, setProgDialog] = useState(null)
  const [qDialog, setQDialog] = useState(null)
  const [tab, setTab] = useState('programs') // 'programs' | 'preschool'

  const load = async () => {
    setLoading(true)
    try {
      const d = await fetchPlannerObservationPrograms({ queries: {} })
      setPrograms(items(d))
    } catch (e) {
      showToast('Failed to load programmes', 'danger')
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const selected = programs.find((p) => p.uuid === selectedId)

  const saveProgram = async () => {
    try {
      if (progDialog.uuid) await updatePlannerObservationProgram(progDialog)
      else await createPlannerObservationProgram(progDialog)
      setProgDialog(null)
      showToast('Saved', 'success')
      await load()
    } catch (e) {
      showToast('Save failed', 'danger')
    }
  }

  const removeProgram = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${p.name}" and its questions?`)) return
    try {
      await deletePlannerObservationProgram({ ids: [p.uuid] })
      if (selectedId === p.uuid) setSelectedId('')
      showToast('Deleted', 'success')
      await load()
    } catch (e) {
      showToast('Delete failed', 'danger')
    }
  }

  const saveQuestion = async () => {
    try {
      const payload = { ...qDialog, program: selectedId }
      if (qDialog.uuid) await updatePlannerObservationQuestion(payload)
      else await createPlannerObservationQuestion(payload)
      setQDialog(null)
      showToast('Saved', 'success')
      await load()
    } catch (e) {
      showToast('Save failed', 'danger')
    }
  }

  const removeQuestion = async (q) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this question?')) return
    try {
      await deletePlannerObservationQuestion({ ids: [q.uuid] })
      showToast('Deleted', 'success')
      await load()
    } catch (e) {
      showToast('Delete failed', 'danger')
    }
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Observation Setup</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Manage the programmes that appear in Observations, their Yes/No questions, and
        the course-name keyword that resolves each programme&apos;s teacher dropdown.
      </Typography>

      <Box display="flex" style={{ gap: 8 }} mt={1} mb={1}>
        <Button
          variant={tab === 'programs' ? 'contained' : 'outlined'}
          color={tab === 'programs' ? 'primary' : 'default'}
          onClick={() => setTab('programs')}
        >
          Programmes
        </Button>
        <Button
          variant={tab === 'preschool' ? 'contained' : 'outlined'}
          color={tab === 'preschool' ? 'primary' : 'default'}
          onClick={() => setTab('preschool')}
        >
          Preschool grades
        </Button>
      </Box>

      {tab === 'preschool' ? (
        <QotSetup />
      ) : (
        <>
      <Box display="flex" style={{ gap: 16 }} mt={2} flexWrap="wrap" alignItems="flex-start">
        {/* Programmes */}
        <Box style={{ minWidth: 300 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Programmes</Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setProgDialog({
                  name: '',
                  course_match: '',
                  is_active: true,
                  display_order: programs.length + 1
                })
              }
            >
              + Add
            </Button>
          </Box>
          {loading ? (
            <CircularProgress />
          ) : (
            programs.map((p) => (
              <Paper
                key={p.uuid}
                variant="outlined"
                style={{
                  padding: 10,
                  marginBottom: 8,
                  borderRadius: 10,
                  cursor: 'pointer',
                  borderColor: selectedId === p.uuid ? '#2d6a4f' : undefined,
                  borderWidth: selectedId === p.uuid ? 2 : 1
                }}
                onClick={() => setSelectedId(p.uuid)}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2">
                      {p.name}{' '}
                      {!p.is_active && <Chip size="small" label="inactive" />}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      keyword: {p.course_match || '—'} · {(p.questions || []).length} questions
                    </Typography>
                  </Box>
                  <Box style={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        setProgDialog({
                          uuid: p.uuid,
                          name: p.name,
                          course_match: p.course_match || '',
                          is_active: p.is_active,
                          display_order: p.display_order
                        })
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      style={{ color: '#9b2226' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        removeProgram(p)
                      }}
                    >
                      Del
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))
          )}
        </Box>

        {/* Questions for selected programme */}
        <Box style={{ flex: 1, minWidth: 320 }}>
          {!selected ? (
            <Typography variant="body2" color="textSecondary">
              Select a programme to manage its questions.
            </Typography>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1">{selected.name} — questions</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setQDialog({
                      section: '',
                      text: '',
                      display_order: (selected.questions || []).length + 1
                    })
                  }
                >
                  + Add question
                </Button>
              </Box>
              {(selected.questions || []).length === 0 && (
                <Typography variant="body2" color="textSecondary">No questions yet.</Typography>
              )}
              {(selected.questions || []).map((q) => (
                <Paper
                  key={q.uuid}
                  variant="outlined"
                  style={{ padding: 10, marginBottom: 6, borderRadius: 8 }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box pr={1}>
                      {!!q.section && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ textTransform: 'uppercase', fontWeight: 600 }}
                        >
                          {q.section}
                        </Typography>
                      )}
                      <Typography variant="body2">{q.text}</Typography>
                    </Box>
                    <Box style={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        onClick={() =>
                          setQDialog({
                            uuid: q.uuid,
                            section: q.section || '',
                            text: q.text,
                            display_order: q.display_order
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        style={{ color: '#9b2226' }}
                        onClick={() => removeQuestion(q)}
                      >
                        Del
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* Programme dialog */}
      <Dialog open={!!progDialog} onClose={() => setProgDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{progDialog && progDialog.uuid ? 'Edit programme' : 'Add programme'}</DialogTitle>
        <DialogContent dividers>
          {progDialog && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <TextField
                label="Name"
                value={progDialog.name}
                onChange={(e) => setProgDialog({ ...progDialog, name: e.target.value })}
              />
              <TextField
                label="Course keyword (resolves teachers)"
                helperText="Word-boundary match on course names, e.g. 'Robotics'. Leave blank for no teacher list."
                value={progDialog.course_match}
                onChange={(e) => setProgDialog({ ...progDialog, course_match: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={progDialog.is_active !== false}
                    onChange={(e) => setProgDialog({ ...progDialog, is_active: e.target.checked })}
                  />
                }
                label="Active (shown in Observations)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgDialog(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={saveProgram}
            disabled={!progDialog || !progDialog.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question dialog */}
      <Dialog open={!!qDialog} onClose={() => setQDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{qDialog && qDialog.uuid ? 'Edit question' : 'Add question'}</DialogTitle>
        <DialogContent dividers>
          {qDialog && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <TextField
                label="Section (optional)"
                helperText="e.g. Facilitation — groups questions in the form"
                value={qDialog.section}
                onChange={(e) => setQDialog({ ...qDialog, section: e.target.value })}
              />
              <TextField
                label="Question"
                multiline
                minRows={2}
                value={qDialog.text}
                onChange={(e) => setQDialog({ ...qDialog, text: e.target.value })}
              />
              <TextField
                label="Order"
                type="number"
                value={qDialog.display_order}
                onChange={(e) => setQDialog({ ...qDialog, display_order: e.target.value })}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQDialog(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={saveQuestion}
            disabled={!qDialog || !qDialog.text}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  )
}

export default ObservationSetup
